import React, { useState, useRef } from 'react';
import { store } from '../store';
import Container from '@mui/material/Container';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { gql, useApolloClient } from '@apollo/client';
import { contactUs } from '../graphql/mutations';
import ReCAPTCHA from 'react-google-recaptcha';
import { addSnackBarMessage } from '../store/reducers/snackBarMessages';
import PropTypes from 'prop-types';
import {
    Button,
    Divider,
    FormHelperText,
    Grid,
    Stack,
    TextField,
    CircularProgress,
    FormControl,
    Box,
    Typography,
    Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import * as Yup from 'yup';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { logger } from '../utils/logger';

const validationSchema = Yup.object().shape({
    email: Yup.string().email().required('Email is required'),
    subject: Yup.string().max(255).required('Subject is required'),
    body: Yup.string().required('Body is required')
});

function ContactForm(props) {
    const { subject, body } = props;
    const { user } = useAuthenticator((context) => [context.user]);
    const client = useApolloClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            email: user?.username || '',
            subject: subject || '',
            body: body || ''
        }
    });

    const recaptchaRef = useRef();

    // Watch form fields to determine when to show captcha
    const watchedEmail = watch('email');
    const watchedSubject = watch('subject');
    const watchedBody = watch('body');
    const showCaptcha = (watchedEmail || user?.username) && watchedSubject && watchedBody;

    const onSubmit = async (event) => {
        setIsSubmitting(true);
        const recaptchaValue = recaptchaRef.current?.getValue();
        if (!recaptchaValue) {
            setErrorMessage('Please Verify Captcha.');
            setIsSubmitting(false);
            return false;
        }
        try {
            const contactUsData = {
                email: user ? user.username : event.email,
                subject: event.subject,
                message: event.body,
                captchaToken: recaptchaValue
            };

            await client.mutate({
                mutation: gql(contactUs),
                variables: { input: contactUsData }
            });
            store.dispatch(addSnackBarMessage('Thank you for contacting us, we will reach out to you shortly.'));

            await recaptchaRef.current.reset();

            setIsSubmitting(false);
            setIsSubmitted(true);
            return true;
        } catch (error) {
            logger.log(error);
            setIsSubmitting(false);
            setErrorMessage(error.message);
            return false;
        }
    };

    let form = (
        <Container maxWidth="md" component="main">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                    <Grid size={12}>
                        <Stack spacing={1}>
                            <TextField
                                error={errors.email != undefined ? true : false}
                                helperText={errors.email && errors.email.message}
                                name="email"
                                {...register('email')}
                                label="Email"
                                htmlFor="email"
                                defaultValue={user?.username}
                                slotProps={{
                                    input: { readOnly: user ? true : false }
                                }}
                            />
                        </Stack>
                    </Grid>
                    <Grid size={12}>
                        <Stack spacing={1}>
                            <TextField
                                error={errors.subject != undefined ? true : false}
                                helperText={errors.subject && errors.subject.message}
                                name="subject"
                                {...register('subject')}
                                label="Subject"
                                htmlFor="subject"
                                defaultValue={subject}
                            />
                        </Stack>
                    </Grid>
                    <Grid size={12}>
                        <Stack spacing={1}>
                            <TextField
                                name="body"
                                error={errors.body != undefined ? true : false}
                                {...register('body')}
                                label="Body"
                                multiline
                                rows={6}
                                helperText={errors.body && errors.body.message}
                                defaultValue={body}
                            />
                        </Stack>
                    </Grid>
                    {showCaptcha && (
                        <Grid size={12}>
                            <Stack spacing={1}>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                                    onChange={() => {
                                        setErrorMessage('');
                                    }}
                                />

                                {errorMessage && (
                                    <FormControl error={true}>
                                        <FormHelperText>{errorMessage}</FormHelperText>
                                    </FormControl>
                                )}
                            </Stack>
                        </Grid>
                    )}
                    <Grid size={12}>
                        <Button
                            disableElevation
                            disabled={isSubmitting}
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
                        </Button>
                    </Grid>
                    <Grid sx={{ pb: 10 }} size={12}>
                        <Divider></Divider>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );

    const successMessage = (
        <Container maxWidth="md" component="main">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 6,
                    textAlign: 'center'
                }}
            >
                <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                    Message Sent Successfully!
                </Typography>
                <Alert severity="success" sx={{ mt: 2, mb: 3, maxWidth: 500 }}>
                    Thank you for contacting us. We will review your message and get back to you shortly.
                </Alert>
                <Button variant="outlined" onClick={() => setIsSubmitted(false)} sx={{ mt: 2 }}>
                    Send Another Message
                </Button>
            </Box>
        </Container>
    );

    return isSubmitted ? successMessage : form;
}

ContactForm.propTypes = {
    subject: PropTypes.string,
    body: PropTypes.string,
    captcha: PropTypes.bool
};

export default ContactForm;
